<script setup lang="tsx">
import type { BreadcrumbList, WithContext } from 'schema-dts';
import type { OrdenationDropdownOption } from '~/components/Filters/OrdenationSelect';
import type {
	LinkClickedWithCategory,
	LinkListItem
} from '~/components/LinkList';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { QuadrilateralMap } from '~/core/service/googleMaps/pluscode/types';

import { useAddresses } from '~/composables/useAddresses';
import { useListingInfoLabel } from '~/composables/useListingInfoLabel/useListingInfoLabel';
import { usePropertyResponsiveFilters } from '~/composables/usePropertyResponsiveFilters/usePropertyResponsiveFilters';
import { useSeoTagsListing } from '~/composables/useSeoTagsListing/useSeoTagsListing';
import { useRegionInfos } from '~/composables/useRegionInfos';

import OpenMapVisionButton from '~/components/Buttons/openMapVisionButton.vue';
import { FiltersBar } from '~/components/Filters/FiltersBar/';
import { FiltersSidebar } from '~/components/Filters/FiltersSidebar/';
import { OrdenationSelect } from '~/components/Filters/OrdenationSelect/';
import { LinkList } from '~/components/LinkList/';
import { Breadcrumb } from '~/components/ui/breadcrumb';
import { Skeleton } from '~/components/ui/skeleton/';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapQuadrilateral from '~/core/service/googleMaps/components/GmapQuadrilateral/GmapQuadrilateral.vue';

import {
	getGroupKeysInBounds,
	getQuadrilateralsFromGroupKeys,
	getUnmappedGroupKeys,
	mergeGroupKeyLists,
	updateGroupsCount
} from '~/core/service/googleMaps/pluscode';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { mainRegions } from '~~/data/links/regionLinks';
import {
	dehyphenateString,
	getChangedKeysFilters,
	grammarInflection
} from '~~/utils';

import { joinURL } from 'ufo';
import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useEvents } from '~/composables/events/useEvents';
import { usePropertiesListingJsonLd } from '~/composables/jsonLd/usePropertiesListingJsonLd';
import { useManager } from '~/composables/useListing/manager';
import { DEFAULT_SERVER_PAGINATION } from '~/composables/useListing/pagination';
import {
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PROPERTIES_LISTING_METHODS } from '~~/utils/symbols';
import type { FilterApplied } from '~/types/mixpanel';
import RegionInfo from '~/components/RegionInfo/RegionInfo.vue';
import RegionQuestions from '~/components/RegionQuestions/RegionQuestions.vue';
import type { RegionInfoLinkClickedEmitProps } from '~/components/RegionInfo';

const { searchPropertyEvents, trackFilterApplied } = useEvents();
const { getFilterAppliedValues } = useEventDataFilters();

definePageMeta({
	name: 'PropertiesListing',
	layout: 'properties-listing',
	pageTitle: 'Imóveis',
	key: 'properties-listing-page',
	alias: ['/venda/imoveis', '/aluguel/imoveis']
});

const route = useRoute();

const listingMethods = inject(PROPERTIES_LISTING_METHODS)!;
const mapState = useState<PropertiesListingMapState>('properties-listing-map');

const quadrilaterals = ref<QuadrilateralMap>(new Map());
const gmapRef = ref<null | { mapInstance: google.maps.Map }>(null);
const lastBoundsStringified = shallowRef<string | null>(null);

useAddresses();

const {
	filters,
	isFetching,
	properties,
	hasProperties,
	filteredCount,
	hasLessThenTenProperties,
	isFetchingForTheFirstTime,
	nextPage,
	updateFiltersManager,
	clearFilters
} = useManager();

const { updateSeoMeta, pageTitle, pageDescription } = useSeoTagsListing(
	filters,
	filteredCount,
	properties
);

updateSeoMeta();

const {
	trackMapListingClicked,
	trackBreadcrumbClicked,
	trackCtaViewMoreClicked,
	trackReorderClicked,
	trackEmptyState,
	trackLinkClicked
} = useEvents();

const { filterComponents, numberOfAppliedClosedFilters, appliedFilterKeys } =
	usePropertyResponsiveFilters({
		filters: computed(() => filters.value),
		updateFilters: updateFiltersManager,
		pageName: 'imoveis'
	});

function updateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	updateFiltersManager(event);
}

async function waitForFilteredCount() {
	return await until(() => isNumber(filteredCount.value)).toBeTruthy();
}

async function waitForFetchingToComplete() {
	return await until(() => !isFetching.value).toBeTruthy();
}

async function handleSidebarApplyFilter(newFilters: PropertyFilters) {
	const changedKeys = getChangedKeysFilters(newFilters, filters.value);
	updateFiltersManager(newFilters);
	isSidebarActive.value = false;

	if (changedKeys.length === 0) return;
	await until(() => isFetching.value).toBeTruthy();
	await Promise.all([waitForFetchingToComplete(), waitForFilteredCount()]);

	const filterApplied = getFilterAppliedValues(
		newFilters,
		'moreFilters',
		changedKeys
	);
	filterApplied[MixpanelProps.TOTAL_RESULTS] = filteredCount.value;
	searchPropertyEvents.trackFilterApplied(filterApplied);
}

function handleSidebarClearFilters() {
	clearFilters();
	isSidebarActive.value = false;
}

const isRunningOnServer = import.meta.server;

const infoLabel = useListingInfoLabel(() => {
	const { regions, cities, states } = filters.value;

	return {
		regions: regions || [],
		cities: cities || [],
		regionType: filters.value?.macroregions?.[0] || null,
		state: states?.[0] || ''
	};
});

const getPropertyTypeText = (filters: PropertyFilters, count: number) => {
	if (filters.propertyTypes?.length === 1) {
		const propertyType = filters.propertyTypes[0]!;

		return count > 1
			? PropertyTypePlurals[propertyType]
			: propertyTypesTranslations[propertyType];
	}

	return grammarInflection(count, 'imóvel', 'imóveis');
};
const propertyCountLabel = computed<string>(
	() =>
		`${filteredCount.value} ${getPropertyTypeText(filters.value, filteredCount.value).toLocaleLowerCase()}`
);

const shouldShowNextPageButton = computed<boolean>(() => {
	if (isFetchingForTheFirstTime.value) return false;

	return properties.value.length < filteredCount.value;
});

const breadcrumbItems = computed<
	(BreadcrumbItem & { structuredDataLabel?: string })[]
>(() => {
	const state = dehyphenateString(filters.value.states?.[0] || 'SP');
	const city = dehyphenateString(filters.value.cities?.[0] || 'São Paulo');
	const region = dehyphenateString(filters.value.regions?.[0] || '');

	return region && state && city
		? [
				{
					label: 'Início',
					structuredDataLabel: 'PilarHomes',
					route: '/',
					separator: false
				},
				{
					label: `${city} - ${state}`,
					route: {
						path: PropertyFiltersPresenter.getPathFromFilters({
							states: [state],
							cities: [city]
						})
					},
					separator: true
				},
				{
					label: region,
					separator: true,
					route: {
						path: PropertyFiltersPresenter.getPathFromFilters({
							states: [state],
							cities: [city],
							regions: [region]
						})
					}
				}
			]
		: [];
});

const websiteUrl = useRuntimeConfig().public.ORIGIN;

const breadcrumbListJsonLd = computed<WithContext<BreadcrumbList> | null>(() =>
	breadcrumbItems.value.length
		? ({
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: breadcrumbItems.value.map((item, index) => {
					const path: string =
						typeof item.route === 'string' ? item.route : item.route.path!;

					return {
						'@type': 'ListItem',
						position: index + 1,
						name: item.label,
						item: joinURL(websiteUrl, path)
					};
				})
			} satisfies WithContext<BreadcrumbList>)
		: null
);

useJsonld(() => breadcrumbListJsonLd.value);

const emptyStateData = computed<{ title: string; description: string } | null>(
	() => {
		if (hasProperties.value && hasLessThenTenProperties.value) {
			return {
				title: 'Esses foram os imóveis encontrados',
				description: `Ajuste os filtros para ver mais imóveis ${infoLabel.value}` //
			};
		}

		if (!hasProperties.value) {
			return {
				title: 'Nenhum imóvel encontrado',
				description: `Ajuste os filtros para ver imóveis ${infoLabel.value}`
			};
		}

		return null;
	}
);

whenever(
	() => emptyStateData.value,
	() => {
		trackEmptyState({
			filters: filters.value,
			currentPage: 'Listing Properties',
			propertiesCount: properties.value.length
		});
	}
);
const { regionInfo, regionName } = useRegionInfos(filters);

const handleBoundsChanged = useDebounceFn(
	(bounds?: google.maps.LatLngBounds) => {
		const boundsStringified = bounds?.toUrlValue();

		if (!bounds) return;

		lastBoundsStringified.value = boundsStringified || null;

		const groupKeysInBounds = getGroupKeysInBounds(bounds);
		const unmappedGroupKeys = getUnmappedGroupKeys(
			quadrilaterals.value,
			groupKeysInBounds || []
		);

		const quadrilateralsInBounds =
			getQuadrilateralsFromGroupKeys(unmappedGroupKeys);

		const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
			route.fullPath,
			filters.value
		);

		listingMethods.updateMapFilters({
			...newFilters,
			countPluscodes: groupKeysInBounds
		});

		quadrilateralsInBounds.forEach((quadrilateral, groupKey) => {
			quadrilaterals.value.set(groupKey, quadrilateral);
		});
	},
	300
);

watch(
	() => mapState.value.pluscodes,
	pluscodes => {
		const mergedQuadrilaterals = mergeGroupKeyLists(
			quadrilaterals.value,
			pluscodes
		);
		const mergedAndUpdatedByCountQuadrilaterals = updateGroupsCount(
			mergedQuadrilaterals,
			pluscodes
		);

		quadrilaterals.value = mergedAndUpdatedByCountQuadrilaterals;
	},
	{ immediate: true }
);

const isSidebarActive = shallowRef<boolean>(false);
const bodyElement = ref<HTMLElement | null>(null);
const isBodyScrollLocked = useScrollLock(bodyElement);

onMounted(() => {
	bodyElement.value = document.body;
});

watch(
	isSidebarActive,
	shouldDisableBodyScroll =>
		(isBodyScrollLocked.value = shouldDisableBodyScroll ? true : false)
);

function propertyCardVariant(index: number) {
	return index === 0 ? 'fullFace' : 'whiteBackground';
}

function handleOpenMapVisionButtonClick(clickLocation: 'cta' | 'map') {
	trackMapListingClicked({
		current_page: 'listed properties',
		click_location: clickLocation,
		cta_text: 'Ver imóveis no Mapa',
		destination_url: PropertyFiltersPresenter.getPathFromFilters(
			filters.value,
			{},
			{ page: 'mapa-de-imoveis' }
		),
		property_count: filteredCount.value,
		filter_city: filters.value.cities?.[0],
		filter_region: filters.value.macroregions?.[0],
		filter_neighborhood: filters.value.regions?.[0]
	});
}

function handleBreadcrumbClicked(breadcrumbItem: BreadcrumbItemWithIndex) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.CLICK_HOME]: breadcrumbItem.index === 0,
		[MixpanelProps.CLICK_CITY]: breadcrumbItem.index === 1,
		[MixpanelProps.CLICK_NEIGHBORHOOD]: false,
		[MixpanelProps.CTA_TEXT]: breadcrumbItem.label,
		[MixpanelProps.DESTINATION_URL]:
			(breadcrumbItem.route as Record<string, string>).path || '/'
	});
}
function handleViewMoreClicked(page: string) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: page,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CLICK_COUNT]: clickCount.value++
	});
}

const clickCount = ref(0);

const viewMoreBus = useEventBus<string>('view-more-clicked');
viewMoreBus.on(handleViewMoreClicked);

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	trackReorderClicked({
		[MixpanelProps.CLICK_EXCLUSIVES]: value === 'pilar_exclusivities',
		[MixpanelProps.CLICK_HIGHLIGHTS]: value === 'highlights',
		[MixpanelProps.CLICK_LOWEST_PRICE]: value === 'asking_price_asc',
		[MixpanelProps.CLICK_HIGHEST_PRICE]: value === 'asking_price_desc',
		[MixpanelProps.CLICK_FEWER_PHOTOS]: value === 'images_count_asc',
		[MixpanelProps.CLICK_MORE_PHOTOS]: value === 'images_count_desc',
		[MixpanelProps.CLICK_MORE_RELEVANT]: value === 'default_ordenation'
	});
}

function handleLinkCategoryClicked(link: LinkListItem) {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.LINK_TEXT]: link.name,
		[MixpanelProps.LINK_CATEGORY]: link.name,
		[MixpanelProps.DESTINATION_URL]:
			(link.route as Record<string, string>)?.path || '/'
	});
}

function handleLinkClicked(link: LinkClickedWithCategory) {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.LINK_TEXT]: link.label,
		[MixpanelProps.LINK_CATEGORY]: link.category,
		[MixpanelProps.DESTINATION_URL]:
			(link.route as Record<string, string>)?.path || '/'
	});
}

function handleSearchByCodeEvent(event: FilterApplied) {
	trackFilterApplied(event);
}

function handleRegionClicked({
	ctaText,
	destinationUrl
}: RegionInfoLinkClickedEmitProps) {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.SECTION_NAME]: 'neighborhood information',
		[MixpanelProps.LINK_TEXT]: ctaText,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.LINK_CATEGORY]: 'Informações do bairro'
	});
}

/**
 * O número de imóveis no JSON-LD é limitado a 10 pois não vamos exibir mais do que isso na primeira página,
 * que é o que vem renderizado no servidor. Além disso, se atualizarmos o JSON-LD toda vez que mais imóveis
 * forem carregados no cliente, pode ser que impacte a performance da página.
 **/
usePropertiesListingJsonLd(
	() => properties.value.slice(0, DEFAULT_SERVER_PAGINATION.page),
	{
		pageTitle: () => pageTitle.value,
		pageDescription: () => pageDescription.value
	}
);
</script>

<template>
	<ClientOnly>
		<template #fallback>
			<FiltersBar
				:filters="filters"
				page="imoveis"
				:is-fetching-properties="false"
				:count-properties="0"
				class="h-[5.1875rem]"
			/>
		</template>

		<Teleport to="#filters-bar">
			<FiltersBar
				v-model:is-sidebar-active="isSidebarActive"
				:filters="filters"
				:applied-filter-keys="appliedFilterKeys"
				:number-of-applied-closed-filters="numberOfAppliedClosedFilters"
				:count-properties="filteredCount"
				:is-fetching-properties="isFetching"
				page="imoveis"
				class="h-[5.1875rem]"
				@update:filters="updateFiltersManager($event)"
				@filter-applied-for-code-search="handleSearchByCodeEvent"
			/>
		</Teleport>
	</ClientOnly>

	<ClientOnly>
		<Teleport to="#filters-sidebar">
			<FiltersSidebar
				v-model:is-active="isSidebarActive"
				:filters="filters"
				:filter-components="filterComponents"
				page-name="imoveis"
				:is-fetching-properties="isFetching"
				:count-properties="filteredCount"
				@apply-filters="handleSidebarApplyFilter"
				@clear-filters="handleSidebarClearFilters"
			/>

			<Transition
				:enter-active-class="'transition-opacity duration-300 ease-out'"
				:enter-from-class="'opacity-0'"
				:enter-to-class="'opacity-100'"
				:leave-active-class="'transition-opacity duration-300 ease-in'"
				:leave-from-class="'opacity-100'"
				:leave-to-class="'opacity-0'"
			>
				<div
					v-show="isSidebarActive"
					class="fixed inset-0 z-50 flex h-screen w-screen bg-black/40"
				/>
			</Transition>
		</Teleport>
	</ClientOnly>

	<div
		:class="{ 'pointer-events-none': isSidebarActive }"
		class="relative mt-20 w-full"
	>
		<Container class="flex flex-col gap-y-5 bg-white !pt-5 pb-11 lg:!pt-6">
			<div class="flex w-full items-end justify-between pb-5">
				<div
					v-if="isFetchingForTheFirstTime && !isRunningOnServer"
					class="w-[70%] space-y-1"
				>
					<Skeleton is-active variant="white" class="h-[28px] w-[100px]" />
					<Skeleton is-active variant="white" class="h-[21px] w-[200px]" />
				</div>

				<div
					v-else-if="hasProperties"
					class="flex w-full items-center justify-between"
				>
					<div class="max-w-[70%] text-left">
						<h1
							class="flex flex-col font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary"
						>
							<span>
								{{ propertyCountLabel }}
							</span>
							<span class="text-sm font-normal leading-normal text-primary">
								{{ infoLabel }}
							</span>
						</h1>
					</div>
					<OrdenationSelect
						:sort-by="filters.sortBy"
						:order-by="filters.orderBy"
						@update-selection="updateOrdenation"
						@reorder-clicked="handleReorderClicked"
					/>
				</div>
			</div>

			<div class="property-card-grid pb-5">
				<div
					v-show="
						(!isFetchingForTheFirstTime || isRunningOnServer) && hasProperties
					"
					class="col-span-full h-[84px] md:col-[span_1_/-1] md:row-[1] md:h-[378px]"
				>
					<ClientOnly fallback-tag="div">
						<template #fallback>
							<div
								class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
							>
								<OpenMapVisionButton
									:filters="filters"
									@open-map-vision="handleOpenMapVisionButtonClick('cta')"
								/>
							</div>
						</template>
						<div
							class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat md:hidden"
						>
							<OpenMapVisionButton
								:filters="filters"
								@open-map-vision="handleOpenMapVisionButtonClick('cta')"
							/>
						</div>
						<GmapMap
							ref="gmapRef"
							:center="mapState.mapConfig.center"
							:zoom="13"
							:zoom-control="false"
							:max-zoom="13"
							:min-zoom="13"
							:bounds="mapState.mapConfig.bounds"
							class="hidden h-[84px] md:block md:h-[378px]"
							@bounds-changed="handleBoundsChanged"
						>
							<GmapQuadrilateral
								v-for="[groupKey, quadrilateral] in quadrilaterals"
								:key="`google-maps-group-${groupKey}`"
								:center-coordinates="quadrilateral.centerCoordinates"
								:paths-coordinates="quadrilateral.pathsCoordinates"
								:count="quadrilateral.count"
								:group-key="groupKey"
								:is-selected="!!filters.pluscodesGroup?.includes(groupKey)"
							/>

							<template #visible>
								<OpenMapVisionButton
									:filters="filters"
									@open-map-vision="handleOpenMapVisionButtonClick('map')"
								/>
							</template>
						</GmapMap>
					</ClientOnly>
				</div>

				<template v-if="isFetchingForTheFirstTime">
					<PropertyCardSkeleton
						v-for="index in 10"
						:key="`initial-skeleton-${index}`"
						variant="whiteBackground"
					/>
				</template>

				<template v-else>
					<div
						v-for="(property, index) in properties"
						:key="property.id"
						class="h-[340px] md:h-[378px]"
					>
						<NuxtLink
							:to="`/imovel/${property.commercialId}/${property.slug}`"
							target="_blank"
							rel="noopener noreferrer"
						>
							<PropertyCard
								:variant="propertyCardVariant(index)"
								size="md"
								class="cursor-pointer"
								:property="property"
								section="listed properties"
								:card-position="index"
								:as-tags="{
									addresses: 'h3',
									askingPrice: 'h4',
									infoItems: 'h5'
								}"
							/>
						</NuxtLink>
					</div>
					<template v-if="isFetching">
						<div class="contents [contain:layout] [overflow-anchor:none]">
							<PropertyCardSkeleton
								v-for="index in 9"
								:key="`pagination-skeleton-${index}`"
								variant="whiteBackground"
								style="
									content-visibility: auto;
									contain: style layout;
									overflow-anchor: none;
								"
							/>
						</div>
					</template>
				</template>
			</div>

			<div class="[contain:style_layout] [overflow-anchor:none]">
				<Button
					v-if="shouldShowNextPageButton"
					class="mx-auto mt-5 w-[150px] cursor-pointer px-11 font-medium"
					@click="nextPage"
				>
					<IconPilarCircularProgress
						v-if="isFetching"
						class="h-[26px] animate-spin text-white"
					/>
					<p v-else>Ver mais</p>
				</Button>
			</div>

			<div v-show="emptyStateData" class="w-full">
				<ListingEmptyState
					class="py-24 2xl:py-[100px]"
					title-classes="text-xl md:text-2xl "
					description-classes="text-sm md:text-lg"
					:show-badges="false"
				>
					<template #title>{{ emptyStateData?.title }}</template>
					<template #description>
						{{ emptyStateData?.description }}
					</template>
				</ListingEmptyState>
			</div>
		</Container>

		<Container
			v-show="breadcrumbItems.length"
			class="bg-white-dark-1 !pb-0 pt-8 md:pt-8"
		>
			<Breadcrumb
				:items="breadcrumbItems"
				@breadcrumb-clicked="handleBreadcrumbClicked"
			/>
		</Container>

		<Container class="bg-white-dark-1">
			<LinkList
				title="Procure nas principais localidades por Região"
				:links="mainRegions"
				variant="no-image"
				@link-category-clicked="handleLinkCategoryClicked"
				@link-clicked="handleLinkClicked"
			/>
		</Container>

		<Container
			v-if="regionInfo?.hero"
			class="bg-white-dark-2 !px-0 !py-[28px] md:!px-0"
			main-class="!max-w-full"
		>
			<RegionInfo
				:name="regionInfo?.hero?.title"
				:description-paragraphs="regionInfo?.seo?.discoveryContent?.paragraphs"
				@region-clicked="handleRegionClicked"
			/>
		</Container>

		<Container
			v-if="regionInfo?.hero"
			class="bg-white-dark-3 !px-0 !py-[28px] md:!px-0"
			main-class="!max-w-full"
		>
			<RegionQuestions
				:name="regionName"
				:questions="regionInfo?.questions"
				:title="`Dúvidas Frequentes sobre ${regionName}`"
				content-block-variant="white-dark-3"
				accordion-item-class="bg-white"
				accordion-trigger-as="h2"
			/>
		</Container>

		<Container class="bg-primary-light-4 py-0">
			<NavigationSection />
		</Container>

		<Container class="bg-primary !py-0">
			<PortalFooter />
		</Container>
	</div>
</template>
